
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthForm from '@/components/auth/AuthForm';
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/hooks/useSupabaseClient';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from 'lucide-react';
import AuthInfoPanel from '@/components/auth/AuthInfoPanel';

const Auth = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (user && !loading) {
      console.log('User is authenticated, redirecting to dashboard');
      toast({
        title: "Inloggning lyckades",
        description: "Välkommen till dashboarden",
      });
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  // Clear any errors when changing tabs
  const handleTabChange = (value: string) => {
    setError(null);
    setActiveTab(value as 'login' | 'register');
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - Auth form */}
      <div className="w-full md:w-3/5 flex flex-col p-6 md:p-10 lg:p-14">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center text-darkgray hover:text-accent transition-colors">
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>Tillbaka till startsidan</span>
          </Link>
        </div>
        
        <div className="flex-grow flex flex-col justify-center max-w-md mx-auto w-full">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-darkgray mb-2">
              {activeTab === 'login' ? 'Logga in' : 'Skapa konto'}
            </h1>
            <p className="text-darkgray/70 text-sm">
              {activeTab === 'login' 
                ? 'Välkommen tillbaka! Logga in för att fortsätta.' 
                : 'Skapa ett konto för att komma igång.'}
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="login" className="w-full" onValueChange={handleTabChange}>
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="login">Logga in</TabsTrigger>
              <TabsTrigger value="register">Skapa konto</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <AuthForm type="login" onError={setError} />
            </TabsContent>
            
            <TabsContent value="register">
              <AuthForm type="register" onError={setError} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right side - Yellow gradient with info */}
      <AuthInfoPanel />
    </div>
  );
};

export default Auth;
